import React from 'react';
import { Navigate } from 'react-router-dom';
import {
  Apartment,
  Assessment,
  Badge,
  Build,
  Business,
  BuildCircle,
  Chat,
  DirectionsCar,
  Email,
  EventNote,
  FolderShared,
  Group,
  MarkEmailRead,
  Payments,
  PersonAdd,
  Place,
  PinDrop,
  QueryStats,
  Receipt,
  ReceiptLong,
  Rule,
  Settings,
  Tune,
  Timeline,
  ToggleOn,
  TrendingUp,
  WhatsApp,
} from '@mui/icons-material';
import { CompanyReservationsPage } from '../pages/CompanyReservationsPage';
import { CompanyLocationsPage } from '../pages/CompanyLocationsPage';
import { CompanyProfilePage } from '../pages/CompanyProfilePage';
import { DomainSettingsPage } from '../pages/DomainSettingsPage';
import { EmailAutomationPage } from '../pages/EmailAutomationPage';
import { EmailSettingsPage } from '../pages/EmailSettingsPage';
import { EmailTemplatesPage } from '../pages/EmailTemplatesPage';
import { CustomerManagementPage } from '../pages/CustomerManagementPage';
import { DriverManagementPage } from '../pages/DriverManagementPage';
import { EmployeeManagementPage } from '../pages/EmployeeManagementPage';
import { ExpenseManagementPage } from '../pages/ExpenseManagementPage';
import { FinancialReportsPage } from '../pages/FinancialReportsPage';
import { FleetManagementPage } from '../pages/FleetManagementPage';
import { FleetServiceTemplatesPage } from '../pages/FleetServiceTemplatesPage';
import { InvoicesPage } from '../pages/InvoicesPage';
import { MaintenancePage } from '../pages/MaintenancePage';
import { PaymentsPage } from '../pages/PaymentsPage';
import { RentalManagementPage } from '../pages/RentalManagementPage';
import { RentalTypesManagementPage } from '../pages/RentalTypesManagementPage';
import { ReportsPage } from '../pages/ReportsPage';
import { RevenueReportPage } from '../pages/reports/RevenueReportPage';
import { VehiclePerformanceReportPage } from '../pages/reports/VehiclePerformanceReportPage';
import { ServicesManagementPage } from '../pages/ServicesManagementPage';
import { SystemSettingsPage } from '../pages/SystemSettingsPage';
import { UserManagementPage } from '../pages/UserManagementPage';
import { VehicleTimelinePage } from '../pages/VehicleTimelinePage';
import { WhatsAppSettingsPage } from '../pages/WhatsAppSettingsPage';
import { WhatsAppTemplatesPage } from '../pages/WhatsAppTemplatesPage';
import {
  APP_ROUTES,
  buildCompanyGroupRoutePath,
  COMPANY_ROUTE_GROUP_SEGMENTS,
  CompanyRouteGroupKey,
} from '../routing/appRoutes';
import type { User } from '../types/auth';

type Translate = (key: string, defaultValue?: string) => string;

export type CompanyNavigationTone =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning';

export type CompanyNavigationGroupId = CompanyRouteGroupKey;

export interface CompanyNavigationGroupDefinition {
  id: CompanyNavigationGroupId;
  segment: string;
  titleKey: string;
  titleDefault: string;
  icon: React.ElementType;
  tone: CompanyNavigationTone;
}

export interface CompanyRouteDefinition {
  id: string;
  groupId: CompanyNavigationGroupId;
  segment: string;
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
  path: string;
  icon: React.ElementType;
  component: React.ComponentType;
  requiredRole?: string;
  requiredPermission?: keyof User['permissions'];
  showInSidebar?: boolean;
  showOnDashboard?: boolean;
  requiresMultipleLocations?: boolean;
  requiresEmailConnectivity?: boolean;
}

interface CompanyRouteConfig extends Omit<CompanyRouteDefinition, 'path'> {}

export interface CompanyNavigationFeatureFlags {
  multipleLocationsEnabled?: boolean;
  emailConnectivityEnabled?: boolean;
}

export interface CompanyNavigationItem extends CompanyRouteDefinition {
  title: string;
  description: string;
}

export interface CompanyNavigationGroup extends CompanyNavigationGroupDefinition {
  title: string;
  items: CompanyNavigationItem[];
}

const SuperuserUserManagementPage: React.FC = () => <UserManagementPage includeSuperusers />;
const LegacyBillingProfilesRedirectPage: React.FC = () => (
  <Navigate to={getCompanyRoutePath('customers')} replace />
);

const isAccessibleForRole = (item: CompanyRouteDefinition, role?: string) =>
  role === 'SUPERUSER' || !item.requiredRole || item.requiredRole === role;

const hasRequiredPermission = (
  item: CompanyRouteDefinition,
  role?: string,
  permissions?: User['permissions']
) =>
  role === 'SUPERUSER' ||
  !item.requiredPermission ||
  Boolean(permissions?.[item.requiredPermission]);

const isVisibleOnSurface = (
  item: CompanyRouteDefinition,
  surface: 'sidebar' | 'dashboard'
) => (surface === 'sidebar' ? item.showInSidebar !== false : item.showOnDashboard !== false);

const isVisibleForFeatures = (
  item: CompanyRouteDefinition,
  features?: CompanyNavigationFeatureFlags
) =>
  (!item.requiresMultipleLocations || Boolean(features?.multipleLocationsEnabled)) &&
  (!item.requiresEmailConnectivity || Boolean(features?.emailConnectivityEnabled));

const translateRouteDefinition = (
  item: CompanyRouteDefinition,
  t: Translate
): CompanyNavigationItem => ({
  ...item,
  title: t(item.titleKey, item.titleDefault),
  description: t(item.descriptionKey, item.descriptionDefault),
});

export const COMPANY_DASHBOARD_PATH = APP_ROUTES.company.dashboard;
export const COMPANY_NAVIGATION_STORAGE_KEY = 'rentman.company-navigation.expanded-groups';

export const COMPANY_NAVIGATION_GROUPS: CompanyNavigationGroupDefinition[] = [
  {
    id: 'operations',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS.operations,
    titleKey: 'nav.operations',
    titleDefault: 'Operations',
    icon: EventNote,
    tone: 'primary',
  },
  {
    id: 'fleet-management',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS['fleet-management'],
    titleKey: 'nav.fleetManagement',
    titleDefault: 'Fleet Management',
    icon: DirectionsCar,
    tone: 'info',
  },
  {
    id: 'accountancy',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS.accountancy,
    titleKey: 'nav.accountancy',
    titleDefault: 'Accountancy',
    icon: Receipt,
    tone: 'success',
  },
  {
    id: 'master-data',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS['master-data'],
    titleKey: 'nav.masterData',
    titleDefault: 'Master Data',
    icon: FolderShared,
    tone: 'warning',
  },
  {
    id: 'people-access',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS['people-access'],
    titleKey: 'nav.peopleAccess',
    titleDefault: 'People & Access',
    icon: Group,
    tone: 'secondary',
  },
  {
    id: 'reports',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS.reports,
    titleKey: 'nav.reports',
    titleDefault: 'Reports',
    icon: Assessment,
    tone: 'primary',
  },
  {
    id: 'settings',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS.settings,
    titleKey: 'nav.settings',
    titleDefault: 'Settings',
    icon: Settings,
    tone: 'info',
  },
  {
    id: 'superuser',
    segment: COMPANY_ROUTE_GROUP_SEGMENTS.superuser,
    titleKey: 'nav.superuser',
    titleDefault: 'SUPERUSER',
    icon: Apartment,
    tone: 'secondary',
  },
];

const COMPANY_ROUTE_CONFIGS: CompanyRouteConfig[] = [
  {
    id: 'rentals',
    groupId: 'operations',
    segment: 'rentals',
    titleKey: 'nav.rentals',
    titleDefault: 'Rentals',
    descriptionKey: 'companyDashboard.tools.rentalManagement.description',
    descriptionDefault: 'Manage reservations, create walk-in bookings, and handle inspections',
    icon: EventNote,
    component: RentalManagementPage,
  },
  {
    id: 'company-reservations',
    groupId: 'operations',
    segment: 'reservations',
    titleKey: 'nav.reservations',
    titleDefault: 'Reservations',
    descriptionKey: 'companyDashboard.tools.reservations.description',
    descriptionDefault: 'View all reservations',
    icon: EventNote,
    component: CompanyReservationsPage,
    showInSidebar: false,
    showOnDashboard: false,
  },
  {
    id: 'vehicle-timeline',
    groupId: 'operations',
    segment: 'vehicle-timeline',
    titleKey: 'nav.vehicleTimeline',
    titleDefault: 'Calendar',
    descriptionKey: 'companyDashboard.tools.vehicleTimeline.description',
    descriptionDefault: 'View vehicle availability and upcoming reservations at a glance',
    icon: Timeline,
    component: VehicleTimelinePage,
  },
  {
    id: 'fleet',
    groupId: 'fleet-management',
    segment: 'fleet',
    titleKey: 'nav.fleet',
    titleDefault: 'Fleet',
    descriptionKey: 'fleet.subtitle',
    descriptionDefault: 'Manage your vehicle inventory',
    icon: DirectionsCar,
    component: FleetManagementPage,
  },
  {
    id: 'maintenance',
    groupId: 'fleet-management',
    segment: 'maintenance',
    titleKey: 'nav.maintenance',
    titleDefault: 'Maintenance',
    descriptionKey: 'companyDashboard.tools.maintenance.description',
    descriptionDefault: 'Track insurance, inspections, taxes, service schedules, and vehicle history',
    icon: BuildCircle,
    component: MaintenancePage,
  },
  {
    id: 'invoices',
    groupId: 'operations',
    segment: 'invoices',
    titleKey: 'nav.invoices',
    titleDefault: 'Invoices',
    descriptionKey: 'companyDashboard.tools.invoices.description',
    descriptionDefault: 'Manage billing and invoices',
    icon: Receipt,
    component: InvoicesPage,
  },
  {
    id: 'payments',
    groupId: 'superuser',
    segment: 'payments',
    titleKey: 'nav.payments',
    titleDefault: 'Payments',
    descriptionKey: 'companyDashboard.tools.payments.description',
    descriptionDefault: 'Collect payments and reconcile customer invoices',
    icon: Payments,
    component: PaymentsPage,
    requiredRole: 'SUPERUSER',
  },
  {
    id: 'expenses',
    groupId: 'superuser',
    segment: 'expenses',
    titleKey: 'nav.expenses',
    titleDefault: 'Expenses',
    descriptionKey: 'expenses.subtitle',
    descriptionDefault: 'Track and manage one-time, recurring, and payroll expenses',
    icon: ReceiptLong,
    component: ExpenseManagementPage,
    requiredRole: 'SUPERUSER',
  },
  {
    id: 'customers',
    groupId: 'master-data',
    segment: 'customers',
    titleKey: 'nav.customers',
    titleDefault: 'Customers',
    descriptionKey: 'companyDashboard.tools.customers.description',
    descriptionDefault: 'Manage customers, company accounts, and invoice recipients',
    icon: Business,
    component: CustomerManagementPage,
  },
  {
    id: 'drivers',
    groupId: 'master-data',
    segment: 'drivers',
    titleKey: 'nav.drivers',
    titleDefault: 'Drivers',
    descriptionKey: 'companyDashboard.tools.drivers.description',
    descriptionDefault: 'Manage driver identity, license details, and customer links',
    icon: PersonAdd,
    component: DriverManagementPage,
  },
  {
    id: 'billing-profiles-legacy',
    groupId: 'master-data',
    segment: 'billing-profiles',
    titleKey: 'nav.customers',
    titleDefault: 'Customers',
    descriptionKey: 'companyDashboard.tools.customers.description',
    descriptionDefault: 'Manage customers, company accounts, and invoice recipients',
    icon: Business,
    component: LegacyBillingProfilesRedirectPage,
    showInSidebar: false,
    showOnDashboard: false,
  },
  {
    id: 'services',
    groupId: 'master-data',
    segment: 'services',
    titleKey: 'nav.services',
    titleDefault: 'Services',
    descriptionKey: 'companyDashboard.tools.services.description',
    descriptionDefault: 'Create and maintain service templates and add-ons',
    icon: Place,
    component: ServicesManagementPage,
  },
  {
    id: 'fleet-services',
    groupId: 'master-data',
    segment: 'fleet-services',
    titleKey: 'nav.fleetServices',
    titleDefault: 'Fleet Services',
    descriptionKey: 'companyDashboard.tools.fleetServices.description',
    descriptionDefault: 'Define recurring service intervals and defaults for the whole fleet',
    icon: Build,
    component: FleetServiceTemplatesPage,
  },
  {
    id: 'rental-types',
    groupId: 'master-data',
    segment: 'rental-types',
    titleKey: 'nav.rentalTypes',
    titleDefault: 'Rental Types',
    descriptionKey: 'companyDashboard.tools.rentalTypes.description',
    descriptionDefault: 'Create and maintain the Person/Company rental type categories',
    icon: Rule,
    component: RentalTypesManagementPage,
  },
  {
    id: 'employees',
    groupId: 'people-access',
    segment: 'employees',
    titleKey: 'nav.employees',
    titleDefault: 'Employees',
    descriptionKey: 'companyDashboard.tools.employees.description',
    descriptionDefault: 'Register and maintain your employee master data',
    icon: Group,
    component: EmployeeManagementPage,
  },
  {
    id: 'users',
    groupId: 'people-access',
    segment: 'users',
    titleKey: 'nav.users',
    titleDefault: 'Users',
    descriptionKey: 'companyDashboard.tools.users.description',
    descriptionDefault: 'Create application users, assign permissions, and link them to employees',
    icon: Badge,
    component: UserManagementPage,
  },
  {
    id: 'reports',
    groupId: 'reports',
    segment: 'reports',
    titleKey: 'nav.reports',
    titleDefault: 'Reports',
    descriptionKey: 'companyDashboard.tools.reports.description',
    descriptionDefault: 'View business analytics and operational reporting',
    icon: Assessment,
    component: ReportsPage,
    requiredPermission: 'canViewReports',
  },
  {
    id: 'revenue-report',
    groupId: 'reports',
    segment: 'revenue-report',
    titleKey: 'nav.revenueReport',
    titleDefault: 'Revenue Report',
    descriptionKey: 'companyDashboard.tools.revenueReport.description',
    descriptionDefault: 'Track completed-rental revenue over time and by vehicle class',
    icon: TrendingUp,
    component: RevenueReportPage,
    requiredPermission: 'canViewReports',
    showOnDashboard: false,
  },
  {
    id: 'vehicle-report',
    groupId: 'reports',
    segment: 'vehicle-report',
    titleKey: 'nav.vehicleReport',
    titleDefault: 'Vehicle Performance Report',
    descriptionKey: 'companyDashboard.tools.vehicleReport.description',
    descriptionDefault: 'Revenue, operational costs and profit per vehicle',
    icon: DirectionsCar,
    component: VehiclePerformanceReportPage,
    requiredPermission: 'canViewReports',
    showOnDashboard: false,
  },
  {
    id: 'financial-reports',
    groupId: 'reports',
    segment: 'financial-reports',
    titleKey: 'nav.financialReports',
    titleDefault: 'Total Profit Report',
    descriptionKey: 'companyDashboard.tools.financialReports.description',
    descriptionDefault: 'Company-wide revenue, expenses, and net profit',
    icon: QueryStats,
    component: FinancialReportsPage,
    requiredPermission: 'canViewReports',
    showOnDashboard: false,
  },
  {
    id: 'company-profile',
    groupId: 'settings',
    segment: 'company',
    titleKey: 'nav.company',
    titleDefault: 'Company',
    descriptionKey: 'companyDashboard.tools.company.description',
    descriptionDefault:
      'Manage your company identity, legal details, branding, address, banking, and business contacts',
    icon: Business,
    component: CompanyProfilePage,
  },
  {
    id: 'company-locations',
    groupId: 'settings',
    segment: 'locations',
    titleKey: 'nav.locations',
    titleDefault: 'Locations',
    descriptionKey: 'companyDashboard.tools.locations.description',
    descriptionDefault: 'Manage company branches used for vehicle assignment and rental pickup and return',
    icon: PinDrop,
    component: CompanyLocationsPage,
    requiresMultipleLocations: true,
  },
  {
    id: 'system-settings',
    groupId: 'settings',
    segment: 'system-settings',
    titleKey: 'nav.systemSettings',
    titleDefault: 'System Settings',
    descriptionKey: 'companyDashboard.tools.systemSettings.description',
    descriptionDefault:
      'Manage numbering sequences and other application-wide operational defaults',
    icon: Tune,
    component: SystemSettingsPage,
  },
  {
    id: 'whatsapp',
    groupId: 'settings',
    segment: 'whatsapp',
    titleKey: 'nav.whatsappSettings',
    titleDefault: 'WhatsApp',
    descriptionKey: 'companyDashboard.tools.whatsappSettings.description',
    descriptionDefault:
      'Connect your WhatsApp Business number, verify the webhook, send test messages, and inspect delivery logs',
    icon: Chat,
    component: WhatsAppSettingsPage,
    requiredRole: 'ADMIN',
  },
  {
    id: 'whatsapp-templates',
    groupId: 'settings',
    segment: 'whatsapp-templates',
    titleKey: 'nav.whatsappTemplates',
    titleDefault: 'WhatsApp Templates',
    descriptionKey: 'companyDashboard.tools.whatsappTemplates.description',
    descriptionDefault: 'Set up the message templates used when messaging customers over WhatsApp',
    icon: WhatsApp,
    component: WhatsAppTemplatesPage,
    requiredRole: 'ADMIN',
  },
  {
    id: 'email-settings',
    groupId: 'settings',
    segment: 'email',
    titleKey: 'nav.emailSettings',
    titleDefault: 'Email',
    descriptionKey: 'companyDashboard.tools.emailSettings.description',
    descriptionDefault:
      'Connect your outgoing mail server and send a test email to confirm it works',
    icon: Email,
    component: EmailSettingsPage,
    requiredRole: 'ADMIN',
    requiresEmailConnectivity: true,
  },
  {
    id: 'email-templates',
    groupId: 'settings',
    segment: 'email-templates',
    titleKey: 'nav.emailTemplates',
    titleDefault: 'Email Templates',
    descriptionKey: 'companyDashboard.tools.emailTemplates.description',
    descriptionDefault: 'Set up the order confirmation and invoice email templates used when emailing customers',
    icon: MarkEmailRead,
    component: EmailTemplatesPage,
    requiredRole: 'ADMIN',
    requiresEmailConnectivity: true,
  },
  {
    id: 'email-automation',
    groupId: 'settings',
    segment: 'email-automation',
    titleKey: 'nav.emailAutomation',
    titleDefault: 'Automatic Emails',
    descriptionKey: 'companyDashboard.tools.emailAutomation.description',
    descriptionDefault: 'Automatically send a status update email to customers when a reservation changes status',
    icon: Rule,
    component: EmailAutomationPage,
    requiredRole: 'ADMIN',
    requiresEmailConnectivity: true,
  },
  {
    id: 'domain-settings',
    groupId: 'superuser',
    segment: 'domain-settings',
    titleKey: 'nav.domainSettings',
    titleDefault: 'Domain Settings',
    descriptionKey: 'companyDashboard.tools.domainSettings.description',
    descriptionDefault: 'Control which features are enabled for your company',
    icon: ToggleOn,
    component: DomainSettingsPage,
    requiredRole: 'SUPERUSER',
  },
  {
    id: 'superuser-users',
    groupId: 'superuser',
    segment: 'users',
    titleKey: 'nav.superuserUsers',
    titleDefault: 'Users',
    descriptionKey: 'companyDashboard.tools.superuserUsers.description',
    descriptionDefault: 'View and manage all application users, including superuser accounts',
    icon: Badge,
    component: SuperuserUserManagementPage,
    requiredRole: 'SUPERUSER',
    showOnDashboard: false,
  },
];

export const COMPANY_ROUTE_DEFINITIONS: CompanyRouteDefinition[] = COMPANY_ROUTE_CONFIGS.map(
  (route) => ({
    ...route,
    path: buildCompanyGroupRoutePath(route.groupId, route.segment),
  })
);

export const getCompanyNavigationGroups = (
  t: Translate,
  role?: string,
  permissions?: User['permissions'],
  surface: 'sidebar' | 'dashboard' = 'sidebar',
  features?: CompanyNavigationFeatureFlags
): CompanyNavigationGroup[] =>
  COMPANY_NAVIGATION_GROUPS.map((group) => ({
    ...group,
    title: t(group.titleKey, group.titleDefault),
    items: COMPANY_ROUTE_DEFINITIONS.filter(
      (item) =>
        item.groupId === group.id &&
        isAccessibleForRole(item, role) &&
        hasRequiredPermission(item, role, permissions) &&
        isVisibleOnSurface(item, surface) &&
        isVisibleForFeatures(item, features)
    ).map((item) => translateRouteDefinition(item, t)),
  })).filter((group) => group.items.length > 0);

export const getCompanyNavigationItems = (
  t: Translate,
  role?: string,
  permissions?: User['permissions'],
  surface: 'sidebar' | 'dashboard' = 'sidebar',
  features?: CompanyNavigationFeatureFlags
): CompanyNavigationItem[] =>
  getCompanyNavigationGroups(t, role, permissions, surface, features).flatMap((group) => group.items);

export const getCompanyRouteDefinition = (routeId: string) =>
  COMPANY_ROUTE_DEFINITIONS.find((item) => item.id === routeId);

export const getCompanyRoutePath = (routeId: string) =>
  getCompanyRouteDefinition(routeId)?.path ?? APP_ROUTES.company.dashboard;

export const getCompanyPageTitle = (
  t: Translate,
  path: string,
  role?: string,
  features?: CompanyNavigationFeatureFlags
) => {
  if (path === COMPANY_DASHBOARD_PATH) {
    return t('dashboard.title', 'Dashboard');
  }

  const matchedItem = COMPANY_ROUTE_DEFINITIONS.find(
    (item) =>
      item.path === path &&
      isAccessibleForRole(item, role) &&
      isVisibleForFeatures(item, features)
  );

  return matchedItem
    ? t(matchedItem.titleKey, matchedItem.titleDefault)
    : t('dashboard.title', 'Dashboard');
};

export const getCompanyActiveNavigationGroupId = (
  path: string,
  role?: string,
  features?: CompanyNavigationFeatureFlags
) => {
  const matchedItem = COMPANY_ROUTE_DEFINITIONS.find(
    (item) =>
      item.path === path &&
      isAccessibleForRole(item, role) &&
      isVisibleForFeatures(item, features)
  );

  return matchedItem?.groupId ?? null;
};
